import type { ResendStatus } from '@kanbu/schema/contracts';
import { extractUtmParams } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useRef, useState } from 'react';

import {
  ChatbotWidget,
  Badge,
  AgentInfoSection,
  AgentTypeToggle,
  type AgentInfoSectionRef,
  type ChatbotWidgetApi,
} from '@/components';
import { AppSettings } from '@/constants';
import { aiCoreClient } from '@/services/ai-core-client';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ui/accordion';
import { Button } from '@/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card';
import { Input } from '@/ui/input';

export const Route = createFileRoute('/$chatId/campaign/')({
  component: CampaignPage,
});

const FramerAnimations = Object.freeze({
  FadeInFromTop: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      mass: 0.8,
    },
  },
  StaggerChildren: {
    initial: { opacity: 0, y: -20 },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  },
});

export function CampaignPage() {
  const { chatId } = Route.useParams();
  const { t } = useLingui();
  const agentInfoSectionRef = useRef<AgentInfoSectionRef>(null);

  const [campaignEmail, setCampaignEmail] = useState(() => {
    const url = new URL(window.location.href);

    return url.searchParams.get('kanbu-campaign-email') ?? '';
  });

  const chatbotApiRef = useRef<ChatbotWidgetApi>(null);
  const [registrationVisible, setRegistrationVisible] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<
    ResendStatus | 'error' | null
  >(null);

  const { data } = useQuery({
    queryKey: ['agentSummary', chatId],
    queryFn: () => aiCoreClient.agents.summary({ agentId: chatId }),
  });

  const { mutate: resendRegistration, isPending: isResendingRegistration } =
    useMutation({
      mutationFn: (email: string) =>
        aiCoreClient.auth.register.resend({
          chatId,
          email,
          utmParams: extractUtmParams(window.location.href),
        }),
      onSuccess: data => {
        setRegistrationResult(data.status);
      },
      onError: () => {
        setRegistrationResult('error');
      },
    });

  return (
    <div className='relative min-h-screen bg-background px-4 py-8 md:p-20 lg:grid grid-cols-2 mx-auto'>
      <div>
        <motion.div
          className='relative z-10 max-w-[600px] mx-auto'
          variants={FramerAnimations.StaggerChildren}
          initial='initial'
          animate='animate'
        >
          <motion.div
            className='text-3xl font-medium'
            variants={FramerAnimations.FadeInFromTop}
          >
            <Trans>
              Welcome to the
              <span className='text-violet-500'> testing </span>
              of your AI assistant!
            </Trans>
          </motion.div>

          <motion.p
            className='text-sm mt-2'
            variants={FramerAnimations.FadeInFromTop}
          >
            <Trans>I am your AI assistant</Trans>,{' '}
            <a
              href='https://kanbu.ai'
              target='_blank'
              rel='noreferrer'
              className='text-primary underline'
            >
              Kanbu.ai
            </a>{' '}
            ,
            <Trans>
              now I will show you how I can communicate with customers for you.{' '}
              <strong className='font-medium'>
                I am trained on a sample of your data and ready to try out.
              </strong>
            </Trans>
          </motion.p>

          <motion.div variants={FramerAnimations.FadeInFromTop}>
            <Accordion
              type='single'
              collapsible
              defaultValue='item-1'
              className='mt-8'
            >
              <AccordionItem value='item-1'>
                <AccordionTrigger>
                  <Trans>1. Check the learned data</Trans>
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    <Trans>
                      I am already trained on a sample of your data from URL
                      addresses. Check the uploaded data in the
                      <strong className='font-medium'>
                        {' '}
                        Sources I know
                      </strong>{' '}
                      section.
                    </Trans>
                  </p>
                  <div className='flex flex-row items-center gap-4 mt-4 justify-between'>
                    <Button
                      data-cta='playground-campaign-check-learned-data-button'
                      onClick={() => {
                        agentInfoSectionRef.current?.openSourcesAccordion();
                      }}
                      size='sm'
                    >
                      <Trans>Open sources</Trans>
                    </Button>

                    <AgentTypeToggle
                      chatId={chatId}
                      onECommerceToggle={enabled => {
                        if (enabled) {
                          agentInfoSectionRef.current?.openFeedsAccordion();
                        }
                      }}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value='item-2'>
                <AccordionTrigger>
                  <Trans>2. Ask me common questions from your customers</Trans>
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    <Trans>
                      Click to open the{' '}
                      <strong className='font-medium'>
                        chat window in the lower right corner of the screen
                      </strong>
                      .
                    </Trans>{' '}
                    <Trans>
                      Ask me from the position of your customer. I can handle
                      answers like a{' '}
                      <strong className='font-medium'>real colleague</strong>,
                      according to your selected websites listed below.
                    </Trans>
                  </p>
                  <Button
                    data-cta='playground-campaign-open-chatbot-button'
                    onClick={() => chatbotApiRef.current?.open()}
                    size='sm'
                    className='mt-4'
                  >
                    <Trans>Open chatbot</Trans>
                  </Button>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value='item-3'>
                <AccordionTrigger>
                  <Trans>3. Upload me to your website</Trans>
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    <Trans>
                      <strong className='font-medium'>Meet my team</strong> and
                      they will help you complete your AI agent. Alternatively,
                      proceed on your own and you can have an AI chatbot on your
                      website
                    </Trans>{' '}
                    <strong className='font-medium'>
                      <Trans>in minutes</Trans>
                    </strong>{' '}
                    <Trans>for</Trans>{' '}
                    <strong className='font-medium'>
                      <Trans>30 days for free</Trans>
                    </strong>
                    .
                  </p>
                  <div className='flex flex-row justify-between mt-4'>
                    <div>
                      <Button
                        data-cta='playground-campaign-arrange-consultation-button'
                        size='sm'
                        asChild
                      >
                        <a
                          href='https://app.reclaim.ai/m/kanbu/demo'
                          target='_blank'
                          rel='noreferrer'
                        >
                          <Trans>Arrange a consultation</Trans>
                        </a>
                      </Button>
                    </div>
                    <div>
                      <Button
                        data-cta='playground-campaign-continue-without-support-button'
                        onClick={() => setRegistrationVisible(true)}
                        size='sm'
                        variant='outline'
                      >
                        <Trans>Continue without support</Trans>
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>

          {registrationVisible && (
            <motion.div variants={FramerAnimations.FadeInFromTop}>
              <Card className='mt-2'>
                <CardHeader>
                  <CardTitle>
                    <Trans>Complete registration</Trans>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {registrationResult ? (
                    <>
                      <p>
                        <Badge
                          variant={
                            registrationResult === 'error' ? 'error' : 'success'
                          }
                        >
                          {registrationResult === 'email-sent' ? (
                            <Trans>Registration email has been sent</Trans>
                          ) : registrationResult === 'already-verified' ? (
                            <Trans>
                              You are already verified. Please continue to the
                              next step.
                            </Trans>
                          ) : (
                            <Trans>
                              There was an error sending the registration email
                            </Trans>
                          )}
                        </Badge>
                      </p>
                      <p className='mt-2'>
                        <Button
                          data-cta='playground-campaign-view-guide-button'
                          asChild
                          variant='outline'
                          size='sm'
                        >
                          <a
                            href={AppSettings.docs.getStarted}
                            target='_blank'
                            rel='noreferrer'
                          >
                            <Trans>View guide: Getting started</Trans>
                          </a>
                        </Button>
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <Trans>
                          To upload more data sources, modify the behavior or
                          appearance of the AI chatbot{' '}
                          <strong className='font-medium'>
                            enter your email
                          </strong>
                          , to which you will receive a confirmation link for
                          security reasons. Then continue according to our{' '}
                          <a
                            href={AppSettings.docs.homePage}
                            target='_blank'
                            rel='noreferrer'
                            className='text-primary underline font-medium'
                          >
                            instructions
                          </a>
                          .
                        </Trans>
                      </p>
                      <Input
                        data-cta='playground-campaign-email-input'
                        type='email'
                        placeholder={t`Your e-mail`}
                        value={campaignEmail}
                        onChange={e => setCampaignEmail(e.target.value)}
                      />
                      <p className='mt-2'>
                        <Button
                          data-cta='playground-campaign-verify-email-button'
                          size='sm'
                          disabled={isResendingRegistration}
                          loading={isResendingRegistration}
                          onClick={() =>
                            campaignEmail && resendRegistration(campaignEmail)
                          }
                        >
                          <Trans>Verify e-mail</Trans>
                        </Button>
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          <AgentInfoSection
            className='mt-8'
            ref={agentInfoSectionRef}
            summary={data}
            title={<Trans>About Your Agent</Trans>}
            chatId={chatId}
          />
        </motion.div>
      </div>

      <ChatbotWidget chatId={chatId} apiRef={chatbotApiRef} />
    </div>
  );
}
